import { money, count } from "../format";

/**
 * Small self-contained SVG trends. TrendChart is the widget-sized area chart;
 * Sparkline is the inline one-glance version used in funnel step rows.
 * No axes ceremony: first/last date, the max, and the latest value.
 */

function path(values: number[], w: number, h: number, pad: number, max: number): string {
  if (values.length === 0) return "";
  const step = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0;
  const y = (v: number) => h - pad - (max > 0 ? (v / max) * (h - pad * 2) : 0);
  return values.map((v, i) => `${i === 0 ? "M" : "L"}${(pad + i * step).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
}

export function TrendChart({
  values,
  days,
  isMoney,
  currency = "USD",
  color = "var(--keeper)",
}: {
  values: number[];
  days: string[];
  isMoney: boolean;
  currency?: string;
  color?: string;
}) {
  const W = 560;
  const H = 168;
  const PAD = 8;
  const max = Math.max(...values, 1);
  const line = path(values, W, H, PAD, max);
  const area = values.length > 1 ? `${line} L${W - PAD},${H - PAD} L${PAD},${H - PAD} Z` : "";
  const total = values.reduce((a, v) => a + v, 0);
  const fmt = (v: number) => (isMoney ? money(v, currency) : count(v));

  if (values.length === 0 || total === 0) {
    return (
      <div style={{ padding: "36px 0", textAlign: "center", color: "var(--text-faint)", fontSize: 12.5 }}>
        Nothing recorded in this window yet.
      </div>
    );
  }

  return (
    <div style={{ minWidth: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img"
           aria-label="Daily trend">
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--border)" strokeWidth="1" />
        {area && <path d={area} fill={color} opacity="0.08" />}
        <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
      <div className="num" style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-faint)", paddingTop: 6 }}>
        <span>{shortDay(days[0])}</span>
        <span style={{ color: "var(--text-3)" }}>
          {isMoney ? `${fmt(total)} total` : `${fmt(total)} total`} · peak {fmt(max)}
        </span>
        <span>{shortDay(days[days.length - 1])}</span>
      </div>
    </div>
  );
}

export function Sparkline({
  values,
  color = "var(--text-4)",
  width = 132,
  height = 30,
}: {
  values: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  const max = Math.max(...values, 1);
  const line = path(values, width, height, 2, max);
  if (values.length === 0 || values.every((v) => v === 0)) {
    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width, height, display: "block" }} aria-hidden="true">
        <line x1="2" y1={height - 2} x2={width - 2} y2={height - 2} stroke="var(--border)" strokeWidth="1" />
      </svg>
    );
  }
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width, height, display: "block" }} aria-hidden="true">
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function shortDay(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}
